import { Box } from '@mui/material';
import DOMPurify from 'dompurify';
import React from 'react';

interface MarkdownRendererProps {
  content: string;
  sx?: any;
}

const forceLinksToNewTab = (html: string): string => {
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
  return template.innerHTML;
};

/**
 * Markdown parser and renderer component
 * Supports CommonMark features: headers, bold, italic, strikethrough, code blocks, 
 * inline code, links, images, blockquotes, lists, horizontal rules, line breaks
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, sx = {} }) => {
  if (!content) return null;

  const decodeHtmlEntities = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    return value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&amp;/g, '&');
  };

  const looksLikeHtml = (value: string): boolean => {
    // TinyMCE returns real HTML tags (e.g. <p>, <strong>...).
    // Old stored markdown usually does not contain tags.
    return /<\/?[a-zA-Z][\w:-]*(\s[^>]*)?>/i.test(value.trim());
  };

  // Parse (legacy) markdown to HTML
  const parseMarkdown = (text: string): string => {
    if (!text) return '';

    // Escape HTML to prevent XSS for the legacy markdown path
    const escapeHtml = (str: string): string => {
      const map: { [key: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return str.replace(/[&<>"']/g, (m) => map[m]);
    };

    let html = escapeHtml(text);

    // Headers (must be at start of line)
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Strikethrough (~~text~~) - process before bold/italic to avoid conflicts
    html = html.replace(/~~(.*?)~~/gim, '<del>$1</del>');

    // Bold (**text** or __text__)
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/gim, '<strong>$1</strong>');

    // Italic (*text* or _text_, but not if it's part of bold)
    // Process italic after bold to avoid conflicts
    // Match single * that are not part of **
    html = html.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/gim, '<em>$1</em>');
    // Match single _ that are not part of __
    html = html.replace(/(?<!_)_([^_]+?)_(?!_)/gim, '<em>$1</em>');

    // Code blocks (```code```)
    html = html.replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>');

    // Inline code (`code`)
    html = html.replace(/`([^`]*)`/gim, '<code>$1</code>');

    // Images ![alt](url) - process before links
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 4px; margin: 8px 0;" />');

    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Unordered lists (* or - at start of line)
    // First, convert list items
    html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
    // Then wrap consecutive list items in ul tags
    html = html.replace(/(<li>.*?<\/li>(\s*<li>.*?<\/li>)*)/gim, '<ul>$1</ul>');

    // Blockquotes (> text)
    html = html.replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>');
    // Wrap consecutive blockquotes
    html = html.replace(/(<blockquote>.*?<\/blockquote>(\s*<blockquote>.*?<\/blockquote>)*)/gim, '<blockquote>$1</blockquote>');

    // Horizontal rules (--- or ***)
    html = html.replace(/^---$/gim, '<hr />');
    html = html.replace(/^\*\*\*$/gim, '<hr />');

    // Ordered lists (1. 2. etc. at start of line)
    html = html.replace(/^\d+\. (.*$)/gim, '<li>$1</li>');
    // Wrap consecutive ordered list items in ol tags (but not if already in ul)
    html = html.replace(/(?<!<\/ul>)(<li>.*?<\/li>(\s*<li>.*?<\/li>)*)(?!<ul>)/gim, (match) => {
      // Check if this is part of an unordered list
      if (!match.includes('</ul>') && !match.includes('<ul>')) {
        return '<ol>' + match + '</ol>';
      }
      return match;
    });

    // Line breaks
    // Double line breaks = new paragraph
    html = html.replace(/\n\n/gim, '</p><p>');
    // Single line break = <br>
    //html = html.replace(/\n/gim, '<br>');

    // Wrap in paragraph if not already wrapped in a block element
    if (!html.trim().startsWith('<')) {
      html = '<p>' + html + '</p>';
    } else if (!html.trim().startsWith('<h') && !html.trim().startsWith('<ul') && !html.trim().startsWith('<ol') && !html.trim().startsWith('<pre')) {
      // If it starts with a tag but not a block element, wrap in p
      html = '<p>' + html + '</p>';
    }

    return html;
  };

  const normalizedContent = decodeHtmlEntities(content);
  const htmlContent = forceLinksToNewTab(
    looksLikeHtml(normalizedContent)
      ? DOMPurify.sanitize(normalizedContent, {
          USE_PROFILES: { html: true },
          ADD_ATTR: ['target', 'rel'],
        })
      : parseMarkdown(normalizedContent)
  );

  return (
    <Box
      sx={{
        color: '#000',
        '& h1': {
          fontSize: '1.8rem',
          fontWeight: 600,
          marginTop: 2,
          marginBottom: 1,
          lineHeight: 1.2
        },
        '& h2': {
          fontSize: '1.5rem',
          fontWeight: 600,
          marginTop: 2,
          marginBottom: 1,
          lineHeight: 1.3
        },
        '& h3': {
          fontSize: '1.2rem',
          fontWeight: 600,
          marginTop: 1.5,
          marginBottom: 0.5,
          lineHeight: 1.4
        },
        '& p': {
          marginBottom: 1,
          lineHeight: 1.6
        },
        '& ul, & ol': {
          marginLeft: 2,
          marginBottom: 1,
          paddingLeft: 2
        },
        '& ul.tox-checklist > li:not(.tox-checklist--hidden)': {
          listStyle: 'none',
          margin: '0.25em 0',
          position: 'relative',
          paddingLeft: '1.5em',
        },
        '& ul.tox-checklist > li:not(.tox-checklist--hidden)::before': {
          content: '""',
          display: 'inline-block',
          width: '1em',
          height: '1em',
          border: '2px solid #4C4C4C',
          borderRadius: '2px',
          position: 'absolute',
          left: 0,
          top: '0.2em',
          boxSizing: 'border-box',
        },
        '& ul.tox-checklist > li.tox-checklist--checked::before': {
          borderColor: '#6154FE',
          backgroundColor: '#6154FE',
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23fff\' d=\'M10 3L4.5 8.5 2 6\'/%3E%3C/svg%3E")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: 1,
        },
        '& th, & td': {
          border: '1px solid #ddd',
          padding: '8px',
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& th': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          fontWeight: 600,
        },
        '& li': {
          marginBottom: 0.5,
          lineHeight: 1.6
        },
        '& strong': {
          fontWeight: 600
        },
        '& em': {
          fontStyle: 'italic'
        },
        '& del': {
          textDecoration: 'line-through',
          opacity: 0.7
        },
        '& blockquote': {
          borderLeft: '4px solid var(--brand-primary)',
          paddingLeft: '16px',
          margin: '16px 0',
          fontStyle: 'italic',
          color: '#666',
          backgroundColor: 'var(--brand-primary-05)'
        },
        '& hr': {
          border: 'none',
          borderTop: '2px solid #e0e0e0',
          margin: '24px 0'
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '4px',
          margin: '8px 0'
        },
        '& code': {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          padding: '2px 6px',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
          color: '#d63384'
        },
        '& pre': {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          padding: '12px',
          borderRadius: '4px',
          overflow: 'auto',
          marginBottom: 1,
          '& code': {
            backgroundColor: 'transparent',
            padding: 0,
            color: '#333'
          }
        },
        '& a': {
          color: '#2196f3',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        },
        ...sx
      }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default MarkdownRenderer;

